import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import EditRateModal from './EditRateModal';

interface EditRateModalContextType {
  isOpen: boolean;
  takerMarket: TakerMarket | null;
  openModal: (takerMarket: TakerMarket) => void;
  closeModal: () => void;
}

const EditRateModalContext = createContext<EditRateModalContextType | undefined>(undefined);

export const useEditRateModalContext = () => {
  const context = useContext(EditRateModalContext);

  if (!context) {
    throw new Error('useEditRateModalContext must be used within a EditRateModalProvider');
  }

  return context;
};

const EditRateModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [takerMarket, setTakerMarket] = useState<TakerMarket | null>(null);
  const { trackEnterpriseEvent } = useReporting();

  const clearTakerMarket = useCallback(() => {
    // delay clearing takerMarket to allow modal close animation
    setTimeout(() => {
      setTakerMarket(null);
    }, 300);
  }, []);

  const openModal = useCallback(
    (tm: TakerMarket) => {
      setTakerMarket(tm);
      setIsOpen(true);
      trackEnterpriseEvent('edit-rate-modal::opened', {
        marketUuid: tm.marketUuid,
        takerDivisionId: tm.takerDivisionId,
      });
    },
    [trackEnterpriseEvent]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    clearTakerMarket();
    trackEnterpriseEvent('edit-rate-modal::closed');
  }, [clearTakerMarket, trackEnterpriseEvent]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      takerMarket,
      openModal,
      closeModal,
    }),
    [isOpen, takerMarket, openModal, closeModal]
  );

  return <EditRateModalContext.Provider value={contextValue}>{children}</EditRateModalContext.Provider>;
};

const EditRateModalComponent = () => {
  const { isOpen, takerMarket, closeModal } = useEditRateModalContext();

  return takerMarket ? <EditRateModal open={isOpen} takerMarket={takerMarket} onClose={closeModal} /> : null;
};

export const EditRateModalWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <EditRateModalProvider>
      <EditRateModalComponent />
      {children}
    </EditRateModalProvider>
  );
};
